import { useEffect, useState } from "react";
// MUI
import { Box, ClickAwayListener, Stack, Tooltip, tooltipClasses, TooltipProps, Typography } from "@mui/material";

// CUSTOM COMPONENTS
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// TYPES
import { IQueue, IQueueLabel } from "@/Settings/types/QueueMapping/QueueMapping";
import styled from "styled-components";
import { buildQueueLabelsObjectList, validateName } from "@/Settings/helpers/QueueMapping";

// ICONS
import { ReactComponent as InformationIcon } from "@/assets/icons/common/circled_information.svg";

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip
		{...props}
		classes={{ popper: className }}
		PopperProps={{
			modifiers: [
				{
					name: "offset",
					options: {
						offset: [0, -6], // Move 10px down
					},
				},
			],
		}}
	/>
))(() => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "#fff2f2",
		color: "#de4759",
		borderRadius: 5.7,
		border: "1px solid #e10000",
		fontSize: 14,
		fontFamily: "PT Sans Pro Narrow, sans-serif",
	},
}));

const getSelectableOptions = (queueLabels: string[], selectedQueueLabel: IQueueLabel | null | undefined) => {
	if (!queueLabels) {
		return [];
	}

	if (!selectedQueueLabel) {
		return buildQueueLabelsObjectList(queueLabels);
	}

	const isIncluded = queueLabels.includes(selectedQueueLabel.label);
	if (!isIncluded) {
		return buildQueueLabelsObjectList([...queueLabels, selectedQueueLabel.label]);
	}

	return buildQueueLabelsObjectList(queueLabels);
};

const getDisabledOptions = (queues: IQueue[] | null, queueLabels: string[]) => {
	if (queues === null) {
		return [];
	}
	return queueLabels?.filter((label) => {
		return queues.some((queueItem) => {
			return queueItem.name === label;
		});
	});
};

const JUSTIFY_ITEMS = "flex-start";
interface IQueueTitleProps {
	queueLabels: string[];
	isQueueLabelsLoading: boolean;
	queue: IQueue;
	handleQueueName: (title: string) => void;
	queues: IQueue[] | null;
	handleNameError: ({ name, error }: { name: string; error: boolean }) => void;
}

const QueueTitle = (props: IQueueTitleProps) => {
	const { queue, handleQueueName, queues, handleNameError, queueLabels, isQueueLabelsLoading } = props;
	const [selectedQueueLabel, setSelectedQueueLabel] = useState<IQueueLabel | null>();
	const [isTitleEditMode, setIsTitleEditMode] = useState(false);
	const [isNameValid, setIsNameValid] = useState(false);
	const [isFoundInQueueLabels, setIsFoundInQueueLabels] = useState(false);

	const handleIsTitleEditMode = (value: boolean) => {
		setIsTitleEditMode(value);
	};

	const handleSelectQueueLabel = (selected: IQueueLabel) => {
		if (!selected?.label) {
			return;
		}

		let cleanedLabel = selected.label.trim();
		if (selected.label.startsWith("Add ")) {
			cleanedLabel = selected.label.replace("Add ", "").replaceAll('"', "").trim();
		}

		setSelectedQueueLabel(selected);

		const isValid = validateName({ ...queue, name: cleanedLabel }, queues ?? []);

		handleNameError({ name: cleanedLabel, error: !isValid });
		setIsNameValid(isValid);

		if (!isValid) {
			return;
		}

		const found = queueLabels.includes(cleanedLabel);
		setIsFoundInQueueLabels(found);

		if (selectedQueueLabel?.id === queue._id && isValid && queue.name !== cleanedLabel) {
			handleQueueName(cleanedLabel);
		}

		handleIsTitleEditMode(false);
	};

	useEffect(() => {
		setSelectedQueueLabel({ id: queue._id, label: queue.name });
		const isValid = validateName(queue, queues ?? []);
		setIsNameValid(isValid);
		handleNameError({ name: queue.name, error: !isValid });
		const found = queueLabels.includes(queue.name);
		setIsFoundInQueueLabels(found);
	}, [queue]);

	return (
		<ClickAwayListener onClickAway={() => handleIsTitleEditMode(false)}>
			<Stack display={"flex"} direction={"column"} spacing={2} ml={1}>
				{isTitleEditMode ? (
					<Stack display={"flex"} direction={"row"} justifyContent={JUSTIFY_ITEMS} alignItems={"flex-end"} gap={1}>
						<SmartSelector
							addable
							showBy={"label"}
							selectBy={"label"}
							options={getSelectableOptions(queueLabels, selectedQueueLabel)}
							disabledOptions={getDisabledOptions(queues, queueLabels)}
							selected={selectedQueueLabel}
							inputProps={{
								placeholder: "Select an option",
							}}
							loading={isQueueLabelsLoading}
							error={selectedQueueLabel?.label === "New Queue"}
							sx={{ width: 253 }}
							onChangeAction={(option) => handleSelectQueueLabel(option as IQueueLabel)}
						/>
					</Stack>
				) : (
					<Stack display={"flex"} direction={"row"} gap={1}>
						{isNameValid ? (
							<Box display={"flex"} alignItems={"center"} onClick={() => handleIsTitleEditMode(true)}>
								<Typography className="!font-montserrat !text-2xl/[30px] !text-gray-text">
									{selectedQueueLabel?.label}
								</Typography>
							</Box>
						) : (
							<Box display={"flex"} alignItems={"center"} onClick={() => handleIsTitleEditMode(true)}>
								<Typography
									className="!font-montserrat !text-2xl/[30px] !text-error bg-[#fff2f2]"
									border={1}
									borderRadius={1.5}
									px={1}
								>
									{selectedQueueLabel?.label}
								</Typography>
							</Box>
						)}
						{!isFoundInQueueLabels && (
							<StyledTooltip
								title={
									<Typography className="!font-sans text-sm" component="span">
										Queue name not found in existing queues.
									</Typography>
								}
								placement="top-start"
								className="!font-sans text-base"
							>
								<Box>
									<InformationIcon className="fill-red-500" />
								</Box>
							</StyledTooltip>
						)}
					</Stack>
				)}
			</Stack>
		</ClickAwayListener>
	);
};

export default QueueTitle;
